import type { FC, ReactNode } from 'react'
import type { ArticleSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { SampleBadge } from '@/components/elements/sample-notice'

interface ArticleListItemProps {
    article: ArticleSummary
    variant?: 'row' | 'feature' | 'compact'
    headingLevel?: 'h2' | 'h3'
    className?: string
}

/**
 * Journal entry in three densities: `feature` for the lead story,
 * `row` for the archive, `compact` for sidebars and related lists.
 */
const ArticleListItem: FC<ArticleListItemProps> = ({ article, variant = 'row', headingLevel: Heading = 'h3', className }): ReactNode => {
    const href = `/journal/${article.slug}`

    if (variant === 'feature') {
        return (
            <article className={cn('group relative flex flex-col gap-5', className)}>
                <p className='flex flex-wrap items-center gap-3 font-mono text-label uppercase text-muted-foreground'>
                    <span className='text-brand'>{article.categoryLabel}</span>
                    <span aria-hidden='true' className='text-border-strong'>/</span>
                    <time dateTime={article.date}>{formatDate(article.date)}</time>
                    <span aria-hidden='true' className='text-border-strong'>/</span>
                    <span>{article.readingTime} min read</span>
                    {article.sample && <SampleBadge />}
                </p>

                <Heading className='font-heading text-title [font-weight:420] text-foreground'>
                    <Link href={href} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                        {article.title}
                    </Link>
                </Heading>

                <p className='max-w-[52ch] text-lede text-muted-foreground'>
                    {article.excerpt}
                </p>

                <span className='inline-flex items-center gap-2 font-mono text-label uppercase'>
                    Read the article
                    <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 ease-editorial group-hover:translate-x-1' />
                </span>
            </article>
        )
    }

    if (variant === 'compact') {
        return (
            <article className={cn('group relative flex flex-col gap-2 border-t border-border py-5', className)}>
                <p className='font-mono text-caption text-muted-foreground'>
                    <time dateTime={article.date}>{formatDate(article.date)}</time>
                    <span aria-hidden='true' className='mx-2 text-border-strong'>/</span>
                    {article.readingTime} min read
                </p>

                <Heading className='font-heading text-subheading [font-weight:500] text-foreground'>
                    <Link href={href} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                        {article.title}
                    </Link>
                </Heading>

                <p className='font-mono text-label uppercase text-muted-foreground'>{article.categoryLabel}</p>
            </article>
        )
    }

    return (
        <article className={cn('group relative border-b border-border', className)}>
            <div className='grid grid-cols-4 gap-x-4 gap-y-3 py-8 sm:gap-x-6 md:grid-cols-12 lg:gap-x-8'>
                <time dateTime={article.date} className='col-span-4 font-mono text-caption text-muted-foreground md:col-span-2 md:pt-1.5'>
                    {formatDate(article.date)}
                </time>

                <div className='col-span-4 flex flex-col gap-2.5 md:col-span-7'>
                    <Heading className='font-heading text-heading [font-weight:450] text-foreground'>
                        <Link href={href} className='transition-colors duration-200 after:absolute after:inset-0 group-hover:text-brand'>
                            {article.title}
                        </Link>
                    </Heading>

                    <p className='max-w-[60ch] text-body text-muted-foreground'>
                        {article.excerpt}
                    </p>
                </div>

                <div className='col-span-4 flex items-start justify-between gap-4 md:col-span-3 md:pt-1.5'>
                    <div className='flex flex-wrap items-center gap-x-3 gap-y-2 md:flex-col md:items-start'>
                        <span className='font-mono text-label uppercase text-foreground'>{article.categoryLabel}</span>
                        <span className='font-mono text-caption text-muted-foreground'>{article.readingTime} min read</span>
                        {article.sample && <SampleBadge />}
                    </div>

                    <ArrowRight
                        aria-hidden='true'
                        className='mt-0.5 size-4 shrink-0 text-muted-foreground transition-[transform,color] duration-300 ease-editorial group-hover:translate-x-1 group-hover:text-brand'
                    />
                </div>
            </div>
        </article>
    )
}

export default ArticleListItem
